import { useEffect, useMemo } from 'react';
import { graphql, type PageProps, type HeadProps } from 'gatsby';
import { useLocation } from '@reach/router';
import Layout from '../components/Layout';
import MetaTags from '@/components/MetaTags/MetaTags';
import useABTest from '@/hooks/useABTest';
import sendABTestAnalytics from '@/utils/a-b-test/sendABTestAnalytics';
import { isPageTranslated } from '@/utils/localization';
import abTestPagesConfigRaw from '../../netlify/edge-functions/lib/a-b-test-page.config.json';
import abTestComponentsConfigRaw from '../../netlify/edge-functions/lib/a-b-test-component.config.json';
import type {
  ABTestComponentConfig,
  ABTestPageConfig,
} from '../../netlify/edge-functions/lib/types';
import usePersonalization from '@/utils/component-personalization/usePersonalization';
import { useSendPersonalizationAnalytics } from '@/utils/component-personalization/useSendPersonalizationAnalytics';

const abTestComponentsConfig = abTestComponentsConfigRaw as ABTestComponentConfig[];
const abTestPagesConfig = abTestPagesConfigRaw as ABTestPageConfig[];

const GeneralPageTemplate = ({
  data,
  pageContext,
}: PageProps<Queries.GeneralPageTemplateQuery>) => {
  const { pathname } = useLocation();

  const availableLanguages = data.allKontentItemGeneralPage.group.flatMap((item) => item.distinct);

  // Memoize filtered component configs to prevent unnecessary recalculations
  const filteredComponentsConfig = useMemo(
    () => abTestComponentsConfig.filter((config) => config.controlPaths.includes(pathname)),
    [pathname]
  );

  const { elements, abTestId, abTestVariantId, abTestComponents } = useABTest({
    page: data.kontentItemGeneralPage,
    processedABTestConfig: abTestPagesConfig,
    processedABTestComponentsConfig: filteredComponentsConfig,
  });

  const hasAbTest = !!(abTestId && abTestVariantId) || abTestComponents.length > 0;
  const {
    elements: personalizedElements,
    winningCampaigns,
    attributes,
  } = usePersonalization(data.kontentItemGeneralPage);

  useSendPersonalizationAnalytics({
    hasAbTest,
    winningCampaigns,
    attributes,
  });

  useEffect(() => {
    sendABTestAnalytics({
      abTestId,
      abTestVariantId,
      abTestComponents,
    });
  }, [abTestId, abTestVariantId, abTestComponents]);

  return (
    <Layout
      pageContext={pageContext}
      elements={hasAbTest ? elements : personalizedElements}
      availableLanguages={availableLanguages}
    />
  );
};

export const Head = ({
  data,
  pageContext,
}: HeadProps<Queries.GeneralPageTemplateQuery, PageContext>) => {
  const isTranslated = isPageTranslated({
    preferredLanguage: data.kontentItemGeneralPage?.preferred_language,
    pageLanguage: data.kontentItemGeneralPage?.system.language,
  });

  return (
    <MetaTags
      language={pageContext.language}
      metadata={data.kontentItemGeneralPage!.elements}
      shouldNotIndex={!isTranslated || pageContext?.shouldNotIndex}
      hasRssFeed={data.kontentItemGeneralPage!.elements.rss_feed?.value === 'on'}
      isTranslated={isTranslated}
    />
  );
};

export const GeneralPageTemplateQuery = graphql`
  query GeneralPageTemplate($pageCodename: String!, $language: String!) {
    kontentItemGeneralPage(
      preferred_language: { eq: $language }
      system: { codename: { eq: $pageCodename } }
    ) {
      ...GENERAL_PAGE_TEMPLATE
      elements {
        ...A_B_TEST
      }
      __typename
    }

    allKontentItemGeneralPage(filter: { system: { codename: { eq: $pageCodename } } }) {
      group(field: { system: { language: SELECT } }) {
        distinct(field: { system: { language: SELECT } })
      }
    }
  }

  fragment GENERAL_PAGE_TEMPLATE on kontent_item_general_page {
    elements {
      ...TWITTER_META
      ...OPENGRAPH_META
      ...SEO_META
      ...PAGE_LAYOUT
      building_blocks {
        value {
          ... on kontent_item {
            system {
              id
              type
            }
          }
          ...ACCORDION
          ...ANNOUNCEMENT_BUTTON
          ...ARTICLE_DISCOVERY
          ...ARTICLE_POST
          ...BREADCRUMBS
          ...BUILDING_BLOCK_WRAPPER
          ...CARDS_BLOCK
          ...CAROUSEL_SECTION
          ...COLUMN_BLOCK
          ...COMBO_BLOCK
          ...COMPARISON_CARDS
          ...COMPARISON_TABLE
          ...CTA_BLOCK
          ...EDITIONS_DOWNLOAD_BLOCK
          ...EXIT_INTENT_POPUP
          ...FOOTER
          ...FORM_CONTAINER
          ...HEADER
          ...HERO_BLOCK_ARTICLE_DETAIL_1
          ...HERO_BLOCK_ARTICLE_DETAIL_2
          ...HERO_BLOCK_HOMEPAGE
          ...HERO_BLOCK_INDEX
          ...HERO_EMAIL_FORM
          ...HTML
          ...IFRAME
          ...IMAGE_COLUMN_BLOCK
          ...INDUSTRY_LOGO_BLOCK
          ...INTERSTITIAL
          ...JUMP_TO_MENU
          ...LANGUAGE_BLOCK
          ...LANGUAGE_GRID_BLOCK
          ...LANGUAGE_GRID_BLOCK_V2
          ...LANGUAGES_GRID_BLOCK_V3
          ...LOGIN_SECTION
          ...LOGO_BLOCK
          ...MEDIA_CAROUSEL
          ...MID_PAGE_CTA
          ...MID_PAGE_FORM_CONTAINER
          ...MID_PAGE_SEARCH
          ...MODAL
          ...N5050_BLOCK
          ...N5050_CARD_BLOCK
          ...OPEN_SOURCE_SOLUTIONS
          ...PRICING_SLIDER_CONTAINER
          ...PRICING_V2_CONTAINER
          ...QUOTE_BLOCK
          ...QUOTE_BLOCK_V2
          ...RELEASE_DOWNLOAD
          ...ROI_CALCULATOR
          ...SECONDARY_HERO_BLOCK
          ...SECONDARY_NAVIGATION
          ...SECTION_INTRODUCTION
          ...SINGLE_MEDIA
          ...STATBAR_BLOCK
          ...STATBAR_BLOCK_V2
          ...STICKY_SIDEBAR
          ...SUPER_CTA_BLOCK
          ...TABBED_BLOCK
          ...TERTIARY_NAVIGATION
          ...TESTIMONIAL_BLOCK
          ...TIMELINE_BLOCK
          ...TOGGLE_RESOURCES
          ...VULNERABILITY_DISCLOSURE_TABLE
        }
      }
      rss_feed {
        value
      }
      rss_feed_entries {
        value {
          elements {
            title {
              value
            }
          }
        }
      }
      creation_date {
        value
      }
      automatic_download_asset {
        value {
          url
        }
      }
      automatic_download_asset_url {
        value
      }
    }
    preferred_language
    system {
      language
    }
  }
`;
export default GeneralPageTemplate;
